/**
 * @name MenuUser
 * @description Menú de acciones de usuario (Settings, Onboarding, Logout, Language)
 * @author Ricardo Guerra
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import styled from 'styled-components';

import { Environment } from '@config';
import { AuthService, EmployeeService, ResponseHandlerService, SnackbarService } from '@services';
import { Avatar, Icons, MUI, Typography } from '@styles';

const MenuUser = (props) => {
  const { isExternal } = props;
  const [anchorEl, setAnchorEl] = useState(null);
  const [anchorElLang, setAnchorElLang] = useState(null);
  const [user, setUser] = useState({ name: null, job: null, img: null });

  const enterpriseParams = useMemo(() => (
    JSON.parse(localStorage.getItem('ls.infoUsuario'))?.Empresa
  ), []);

  useEffect(() => {
    const employee = JSON.parse(localStorage.getItem('ls.infoUsuario'))?.Empleado;
    if (!employee) {
      return;
    }

    if (isExternal) {
      // Usuario externo
      setUser({
        name: employee.Nombre,
        job: i18n.t('GenericMessages.userExternal'),
        img: employee.Foto || 'https://assets.talentry.io/icons/profile/TAL_Avatar.png'
      });
      return;
    }

    // Usuario interno
    setUser({
      name: `${employee.Nombres} ${employee.Apellidos}`,
      job: employee.Puesto,
      img: employee.Foto && employee.Foto.length > 0 ? employee.Foto : 'https://assets.talentry.io/icons/profile/TAL_Avatar.png'
    });
  }, [isExternal]);

  const openHandler = useCallback((event) => {
    setAnchorEl(event.currentTarget);
    setAnchorElLang(null);
  }, []);

  const closeHandler = useCallback(() => {
    setAnchorEl(null);
    setAnchorElLang(null);
  }, []);

  const openHandlerLang = useCallback((event) => {
    setAnchorElLang(event.currentTarget);
  }, []);

  const closeHandlerLang = useCallback(() => {
    setAnchorElLang(null);
  }, []);

  const goToSettings = useCallback(() => {
    let URL = Environment.getCurrent().domain;
    window.location.href = `${URL.substring(0, URL.length - 2)}talentry/#/configuraperfil`;
  }, []);

  const logout = useCallback(() => {
    AuthService.clearStorage();
    AuthService.logout();
  }, []);

  /**
   * Cambiar el lenguaje de la plataforma
   * @param {string} lang (es, en, pt)
   */
  const changeLanguage = useCallback((lang) => {
    EmployeeService.putProfile({ language: lang })
      .then(() => {
        // Switch Language
        localStorage.setItem('ls.ty-Language', JSON.stringify({ language: lang }));
        i18n.use(initReactI18next).init({ lng: lang });

        EmployeeService.getInfo()
          .then((response) => {
            localStorage.removeItem('ls.infoUsuario');
            localStorage.setItem('ls.infoUsuario', JSON.stringify(response));
          });

        SnackbarService.success(i18n.t('GenericMessages.languageUpdated'));
        setAnchorElLang(null);
        window.location.reload();
      })
      .catch((error) => {
        ResponseHandlerService.error(error);
      });
  }, []);

  return (
    <>
      <Styled.BoxTopbarIcon onClick={openHandler}>
        <MUI.Tooltip title={i18n.t('Topbar.userMenu')}>
          <Styled.BoxAvatar>
            <Avatar
              size="xs"
              src={user.img}
            />
            <Icons.ArrowDown />
          </Styled.BoxAvatar>
        </MUI.Tooltip>
      </Styled.BoxTopbarIcon>

      <Styled.Popover
        open={Boolean(anchorEl)}
        onClose={closeHandler}
        anchorEl={anchorEl}
      >
        <MUI.MenuHeader>
          <Styled.BoxHeader>
            <Typography.Text>{user.name}</Typography.Text>
            <Typography.Text color="greyDarkAlpha">{user.job}</Typography.Text>
          </Styled.BoxHeader>
        </MUI.MenuHeader>

        <Styled.BoxUserActions>
          {/* Configuración */}
          {/* NOTA: Ocultar opción para ID 93 KMPG y ID 1126 KPMG Costa Rica */}
          {(!isExternal && enterpriseParams?.IdEmpresa !== 93 && enterpriseParams?.IdEmpresa !== 1126) && (
            <MUI.MenuItem onClick={goToSettings}>
              <Styled.BoxIcon>
                <Icons.Settings />
              </Styled.BoxIcon>
              <Typography.Text>{i18n.t('Topbar.setting')}</Typography.Text>
            </MUI.MenuItem>
          )}

          {/* Onboarding */}
          {/* !isExternal && ( 
            <MenuItem>
                {i18n.t('Topbar.onboarding')}
            </MenuItem>
          )*/}

          {/* Logout */}
          <MUI.MenuItem onClick={logout}>
            <Styled.BoxIcon>
              <Icons.ExitToApp />
            </Styled.BoxIcon>
            <Typography.Text>{i18n.t('Topbar.logout')}</Typography.Text>
          </MUI.MenuItem>

          {/* Idioma */}
          <MUI.MenuItem onClick={openHandlerLang}>
            <Styled.BoxIcon>
              <Icons.Translate />
            </Styled.BoxIcon>
            <Typography.Text>{i18n.t('Topbar.language')}</Typography.Text>
            <MUI.Box marginLeft="8px">
              <Styled.BoxIcon>
                <Icons.ArrowDown />
              </Styled.BoxIcon>
            </MUI.Box>
          </MUI.MenuItem>
        </Styled.BoxUserActions>
      </Styled.Popover>

      <Styled.PopoverLang
        open={Boolean(anchorElLang)}
        onClose={closeHandlerLang}
        anchorEl={anchorElLang}
        isExternal={isExternal}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <MUI.MenuItem onClick={() => changeLanguage('es')}>
          <Typography.Text>{i18n.t('GenericMessages.es')}</Typography.Text>
        </MUI.MenuItem>
        <MUI.MenuItem onClick={() => changeLanguage('en')}>
          <Typography.Text>{i18n.t('GenericMessages.en')}</Typography.Text>
        </MUI.MenuItem>
        <MUI.MenuItem onClick={() => changeLanguage('pt')}>
          <Typography.Text>{i18n.t('GenericMessages.pt')}</Typography.Text>
        </MUI.MenuItem>
      </Styled.PopoverLang>
    </>
  );
};

MenuUser.propTypes = {
  // Boolean que indica si es usuario externo.
  isExternal: PropTypes.bool.isRequired,
  // History router.
  history: PropTypes.object
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const Styled = {};

Styled.BoxTopbarIcon = styled(MUI.ButtonText)`
  &.MuiButton-root{
    display: flex;
    width: auto;
    height: 60px;
    padding: 8px 12px;
    margin: 0;

    &:hover {
      text-decoration: none;
      background-color: ${(props) => props.theme.colors.greyHover};
      border-radius: 0%;
    }
  }
`;

Styled.BoxAvatar = styled.div`
  display: flex;
  align-items: center;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

Styled.BoxHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

Styled.BoxUserActions = styled.div`
  max-height: 470px;
  overflow-y: auto;
  background: ${(props) => props.theme.colors.white};
  backface-visibility: hidden;
  scrollbar-width: thin !important; /* Scrollbar Firefox */
`;

Styled.BoxIcon = styled.div`
  display: flex;
  margin-right: 12px;

  .MuiSvgIcon-root {
    font-size: 20px;
  }
`;

// *************************************
// GLOBAL STYLED COMPONENTS          
// *************************************

Styled.Popover = styled(MUI.Popover)`
  .MuiPopover-paper {
    min-width: 280px;
    max-height: 520px;
    max-width: 608px;
    left: auto !important;
    right: 16px !important;
    top: 60px !important;
    overflow-y: hidden;
    overflow-x: hidden;
  }
`;

Styled.PopoverLang = styled(MUI.Popover)`
  .MuiPopover-paper {
    max-height: 520px;
    max-width: 608px;
    min-width: 220px;

    // left: auto !important;
    // right: 16px !important;
    // top: ${(props) => !props.isExternal ? 270 : 220}px !important;
  }
`;

export default MenuUser;
